import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { strings } from '/imports/strings';
import { Checkbox } from '/imports/ui/Checkbox';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { Select } from '/imports/ui/Select';
import { Button } from '/imports/ui/Button';
import { Page } from '/imports/ui/Page';
import { modelClass, ConsumptionMeter } from '/imports/api/consumptionMeters';
import { modelClass as utilityBillClass } from '/imports/api/utilityBills';
import { Input } from '/imports/ui/Input';
import { Icon } from '/imports/ui/Icon';
import { Modal } from '/imports/ui/Modal';
import { formatCurrency, formatDate, formatDateTime } from '/imports/utils';
import { FormField } from '/imports/ui/FormField';
import { timezoneOptions } from '/imports/options';
import { PullConsumptionIntervalsModal } from '/imports/ui/PullConsumptionIntervalsModal';
import { AddressInput } from '/imports/ui/AddressInput';
import { ListMethodCatalog } from '/imports/ui/ListMethodCatalog';
import { ConsumptionIntervalsVsTimeChart } from '/imports/ui/ConsumptionIntervalsVsTimeChart';
import { InterconnectionsModal } from '/imports/ui/InterconnectionsModal';
import { useHasConsumptionMeters, useUser } from '/imports/hooks';

const ConsumptionMeterPageStyled = styled(Page)`
`;

/**
 * Formats a date-only string as a localized date, fixing time to noon UTC.
 * @param date - date-only string (YYYY-MM-DD)
 * @returns formatted date string
 */
const formattedDate = (date: string) => formatDate(new Date(`${date}T12:00:00.000Z`));

/**
 * A consumption meter with its utility bills and intervals. The consumptionMeters role may READ it; editing
 * the meter, pulling intervals and managing interconnections stay super-user acts, so those controls are
 * hidden from a role-holder rather than failing on save.
 */
export const ConsumptionMeterPage = () => {
  const { _id } = useParams<any>();
  const user = useUser();
  const canRead = useHasConsumptionMeters();
  const isSuperUser = !!user?.isSuperUser;
  const defaultUpdate: Partial<ConsumptionMeter> = { _id, active: true };
  const [update, setUpdate] = useState<Partial<ConsumptionMeter>>(defaultUpdate);
  const documentRef = useRef<ConsumptionMeter>(undefined);
  const doc = documentRef.current;
  const [showPullModal, setShowPullModal] = useState(false);
  const [showConsumptionModal, setShowConsumptionModal] = useState(false);
  const [showInterconnectionsModal, setShowInterconnectionsModal] = useState(false);
  return <ModelFormPage
    modelClass={modelClass}
    styled={ConsumptionMeterPageStyled}
    permission={() => canRead}
    documentRef={documentRef}
    defaultUpdate={defaultUpdate}
    update={update}
    setUpdate={setUpdate}
    formSpec={[
      [{ field: 'active', input: { type: Checkbox } }],
      [{ field: 'name' }, { field: 'identifier', input: { type: Input, props: { disabled: true } } }],
      [{ field: 'address', input: { type: AddressInput } }],
      [
        { field: 'type', input: { type: Input, props: { disabled: true } } },
        { field: 'timezone', input: { type: Select, props: { options: [{ value: '', label: '' }, ...timezoneOptions()] } } },
        {
          field: 'createdAt', input: () => (
            <FormField key="createdAt" field="createdAt"><label>{strings.CreatedAt()}</label><Input placeholder={formatDateTime(doc?.createdAt)} disabled /></FormField>
          )
        }
      ],
    ]}
    extraButtons={
      <>
        <Button onClick={() => setShowConsumptionModal(true)}>{strings.ViewConsumption()}</Button>
        {isSuperUser && <Button onClick={() => setShowPullModal(true)}>{strings.PullConsumption()}</Button>}
        {isSuperUser && <Button onClick={() => setShowInterconnectionsModal(true)}>{strings.Interconnections()}</Button>}
      </>
    }
    pageFooter={
      <>
        <div>
          <h2>{strings.UtilityBills()}</h2>
          <ListMethodCatalog
            listMethod={utilityBillClass.listMethod}
            columns={[
              { field: 'sourceId', label: 'ID' },
              { field: 'pdf', label: 'PDF', render: (doc) => <a href={doc.pdf} target="_blank" rel="noreferrer"><Icon icon="docs" /></a> },
              { field: 'billingPeriod', render: (doc) => <span>{formattedDate(doc.startDate).substring(0, 5)}→{formattedDate(doc.endDate)}</span> },
              { field: 'consumption', label: strings.NetConsumption(), render: (doc) => <span>{Math.ceil(doc.usage)} kWh</span> },
              { field: 'charges', render: (doc) => <span>{formatCurrency(doc.amount)}</span> },
            ]}
            query={{ meterId: _id }}
            defaultSort={{ startDate: -1 }}
          />
        </div>
        <div>
          {
            showPullModal && doc ?
              <PullConsumptionIntervalsModal meterIds={[doc._id]} onClose={() => setShowPullModal(false)} />
              :
              null
          }
          {
            showConsumptionModal && doc ?
              <ProductionModal onClose={() => setShowConsumptionModal(false)}
                body={<ConsumptionIntervalsVsTimeChart meter={{ ...doc, ...update }} />}
              />
              :
              null
          }
          {
            showInterconnectionsModal && doc ?
              <InterconnectionsModal consumptionMeterId={doc._id} onClose={() => setShowInterconnectionsModal(false)} />
              :
              null
          }
        </div>
      </>
    }
  />;
};

const ProductionModal = styled(Modal)`
  .modal-window {
    width: 85rem;
    max-width: 100%;
    .recharts-responsive-container {
      height: 18rem !important;
    }
  }
`;